/**
 * ПРОДАЖ-розбивка Етап 4 (2026-06-03): Дашборд 4 показників наявності.
 *
 * Власник 2026-06-03 у плані: «створити окрему аналітику для наявності
 * товарів — 4 показника: Залишок / В дорозі / Резерв / Попит».
 *
 * Показує всі 4 показника одночасно в одній таблиці + сумарний підрахунок
 * «доступно до продажу» (Залишок - Резерв).
 *
 * Фільтри:
 * - Організація (default — перша доступна)
 * - На дату (default — кінець дня сьогодні)
 * - Склад (опц.)
 * - Пошук за назвою/кодом
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reports, type InventoryAnalyticsRow } from '@/api/documents';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DatePopover from '@/components/DatePopover';


function endOfToday(): string {
  const d = new Date();
  d.setHours(23, 59, 59, 0);
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? '+' : '-';
  const pad = (n: number) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00${sign}${pad(tz / 60)}:${pad(tz % 60)}`;
}

const fmtQty = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 4 });


export default function InventoryAnalyticsPage() {
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useState('');
  const [onDate, setOnDate] = useState<string>(() => endOfToday());
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [search, setSearch] = useState('');

  if (!orgId && orgList.data?.items[0]) setOrgId(orgList.data.items[0].id);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', orgId],
    queryFn: () => refs.warehouses(orgId),
    enabled: !!orgId,
  });

  const reportQ = useQuery({
    queryKey: ['inventory-analytics', orgId, onDate, warehouseId, search],
    queryFn: () => reports.inventoryAnalytics({
      organization_id: orgId,
      on_date: onDate,
      warehouse_id: warehouseId || undefined,
      q: search || undefined,
    }),
    enabled: !!orgId,
  });

  // Color cells based on values
  const numCell = (v: string, color: string, bold = false) => (
    <span style={{ fontVariantNumeric: 'tabular-nums', color, fontWeight: bold ? 600 : undefined }}>
      {fmtQty(v)}
    </span>
  );

  const cols = useMemo<ColumnDef<InventoryAnalyticsRow>[]>(() => [
    { key: 'nomenclature_code', header: 'Код', width: 110, sortValue: (r) => r.nomenclature_code ?? '', cell: (r) => r.nomenclature_code },
    { key: 'nomenclature_name', header: 'Номенклатура', sortValue: (r) => r.nomenclature_name ?? '', cell: (r) => r.nomenclature_name },
    {
      key: 'uom_short', header: 'Од.', width: 70, align: 'center',
      sortValue: (r) => r.uom_short ?? '',
      cell: (r) => <span style={{ color: '#64748b' }}>{r.uom_short ?? ''}</span>,
    },
    { key: 'warehouse_name', header: 'Склад', width: 150, sortValue: (r) => r.warehouse_name ?? '', cell: (r) => r.warehouse_name ?? '' },
    {
      key: 'stock', header: 'Залишок', width: 120, align: 'right',
      sortValue: (r) => Number(r.stock),
      cell: (r) => numCell(r.stock, '#0f172a'),
    },
    {
      key: 'in_transit', header: 'В дорозі', width: 120, align: 'right',
      sortValue: (r) => Number(r.in_transit),
      cell: (r) => numCell(r.in_transit, '#2563eb'),
    },
    {
      key: 'reserved', header: 'Резерв', width: 120, align: 'right',
      sortValue: (r) => Number(r.reserved),
      cell: (r) => numCell(r.reserved, '#ca8a04'),
    },
    {
      key: 'demanded', header: 'Попит', width: 120, align: 'right',
      sortValue: (r) => Number(r.demanded),
      cell: (r) => numCell(r.demanded, '#9333ea'),
    },
    {
      key: 'available', header: 'Доступно', width: 130, align: 'right',
      sortValue: (r) => Number(r.available),
      cell: (r) => {
        const v = Number(r.available);
        return numCell(r.available, v < 0 ? '#dc2626' : v === 0 ? '#64748b' : '#16a34a', true);
      },
    },
  ], []);

  // Зведені totals
  const rows = reportQ.data?.rows ?? [];
  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => ({
        stock: acc.stock + Number(r.stock),
        reserved: acc.reserved + Number(r.reserved),
        in_transit: acc.in_transit + Number(r.in_transit),
        demanded: acc.demanded + Number(r.demanded),
        available: acc.available + Number(r.available),
      }),
      { stock: 0, reserved: 0, in_transit: 0, demanded: 0, available: 0 },
    );
  }, [rows]);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Аналітика наявності товарів</h2>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: '0.75rem', marginBottom: '0.75rem',
      }}>
        <div className="field">
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>На дату</label>
          <DatePopover
            withTime
            value={onDate.slice(0, 16)}
            onChange={(v) => setOnDate(v + ':00+02:00')}
          />
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">— Усі склади —</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Пошук</label>
          <input
            type="text"
            placeholder="Назва або код товару…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {reportQ.data && rows.length > 0 && (
        <div style={{
          padding: '0.75rem',
          background: '#f8fafc',
          borderRadius: 6,
          marginBottom: '0.75rem',
          fontSize: '0.9rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gap: '0.5rem',
        }}>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Записів</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{rows.length}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Σ Залишок</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#0f172a' }}>{fmtQty(totals.stock)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Σ В дорозі</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#2563eb' }}>{fmtQty(totals.in_transit)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Σ Резерв</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#ca8a04' }}>{fmtQty(totals.reserved)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Σ Попит</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem', color: '#9333ea' }}>{fmtQty(totals.demanded)}</div>
          </div>
          <div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>Σ Доступно</div>
            <div style={{
              fontWeight: 600, fontSize: '1.1rem',
              color: totals.available < 0 ? '#dc2626' : '#16a34a',
            }}>
              {fmtQty(totals.available)}
            </div>
          </div>
        </div>
      )}

      <DataTable<InventoryAnalyticsRow>
        rows={rows}
        columns={cols}
        rowKey={(r) => `${r.nomenclature_id}::${r.warehouse_id ?? ''}`}
        loading={reportQ.isLoading}
        emptyText="Немає даних."
        initialSortKey="nomenclature_name"
      />

      <div style={{
        marginTop: '1rem',
        padding: '0.75rem',
        background: '#fffbeb',
        borderLeft: '3px solid #f59e0b',
        fontSize: '0.85rem',
        color: '#78350f',
      }}>
        <strong>Як читати показники:</strong>
        <ul style={{ margin: '0.3rem 0 0 1rem', padding: 0 }}>
          <li><strong>Залишок</strong> — фактично на складі (Надходження + / Реалізація -).</li>
          <li><strong>В дорозі</strong> — замовлено постачальникам, ще не отримано.</li>
          <li><strong>Резерв</strong> — зарезервовано під рахунки покупцям.</li>
          <li><strong>Попит</strong> — замовлено покупцями (Замовлення покупця), ще не відвантажено.</li>
          <li><strong>Доступно</strong> = Залишок − Резерв. <em>Це те, що можна продати зараз без резервації.</em></li>
        </ul>
      </div>
    </>
  );
}
